import React, { createContext, useContext, useState, useMemo } from 'react';

const TabsContext = createContext(null);

export function Tabs({ defaultValue = undefined, value: controlledValue, onValueChange, children, className = '', ...props }) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const active = isControlled ? controlledValue : internalValue;

  const setValue = (v) => {
    if (!isControlled) setInternalValue(v);
    if (typeof onValueChange === 'function') onValueChange(v);
  };

  const ctx = useMemo(() => ({ value: active, setValue }), [active]);

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function TabsList({ children, className = '', ...props }) {
  return (
    <div role="tablist" className={className} {...props}>
      {children}
    </div>
  );
}

export function TabsTrigger({ value, children, className = '', ...props }) {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    // render a non-interactive button if used outside Tabs
    return (
      <button type="button" className={className} {...props}>
        {children}
      </button>
    );
  }
  const isActive = ctx.value === value;
  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      data-state={isActive ? 'active' : 'inactive'}
      onClick={() => ctx.setValue(value)}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, children, className = '', ...props }) {
  const ctx = useContext(TabsContext);
  const isActive = ctx ? ctx.value === value : false;
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      className={className}
      aria-hidden={!isActive}
      {...props}
    >
      {isActive ? children : null}
    </div>
  );
}

export default Tabs;
